import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, increment } from 'firebase/firestore';
import { db } from '../firebase';
import './TrackPDF.css';

const TrackPDF = () => {
  const { trackingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [viewStartTime, setViewStartTime] = useState(null);

  useEffect(() => {
    if (!trackingId) {
      setError('Invalid tracking ID');
      setLoading(false);
      return;
    }

    const trackView = async () => {
      try {
        const trackingDocRef = doc(db, 'pdfTracking', trackingId);
        const trackingDoc = await getDoc(trackingDocRef);

        if (!trackingDoc.exists()) {
          setError('PDF not found');
          setLoading(false);
          return;
        }

        const trackingData = trackingDoc.data();
        const storageUrl = trackingData.storageUrl;

        if (!storageUrl) {
          setError('PDF URL not found');
          setLoading(false);
          return;
        }

        // Get viewer info
        const userAgent = navigator.userAgent;
        const ipAddress = await getClientIP();
        const referrer = document.referrer || 'direct';
        const email = searchParams.get('email') || null; // Get email from URL parameter
        const timestamp = new Date();

        // Generate a unique viewer ID (based on email if available, otherwise IP + User Agent hash)
        const viewerId = email ? generateViewerId(email) : generateViewerId(userAgent, ipAddress);

        // Check if this is a repeat view
        const existingViews = trackingData.views || [];
        const isRepeatView = existingViews.some(v => v.viewerId === viewerId);

        // Record the view
        const viewData = {
          timestamp: timestamp,
          userAgent,
          ipAddress,
          email: email || null, // Store email if available
          referrer,
          viewerId,
          isRepeatView,
          viewStartTime: timestamp,
        };

        // Update tracking document
        await updateDoc(trackingDocRef, {
          views: arrayUnion(viewData),
          totalViews: increment(1),
          uniqueViews: isRepeatView ? trackingData.uniqueViews || 0 : increment(1),
          lastViewed: timestamp,
        });

        setPdfUrl(storageUrl);
        setViewStartTime(timestamp);
        setLoading(false);

        // Track time viewed - update when user leaves or closes
        const handleBeforeUnload = async () => {
          const timeViewed = Math.floor((new Date() - timestamp) / 1000); // seconds
          
          // Add a final time update
          await updateDoc(trackingDocRef, {
            [`viewTimes.${viewerId}.${timestamp.toISOString()}`]: timeViewed,
          });
        };

        // Track time viewed periodically while page is open
        const timeTrackingInterval = setInterval(async () => {
          const timeViewed = Math.floor((new Date() - timestamp) / 1000); // seconds
          
          // Store time viewed in a separate field
          await updateDoc(trackingDocRef, {
            [`viewTimes.${viewerId}.${timestamp.toISOString()}`]: timeViewed,
          });
        }, 10000); // Update every 10 seconds

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Clean up interval when component unmounts
        return () => {
          clearInterval(timeTrackingInterval);
          window.removeEventListener('beforeunload', handleBeforeUnload);
          // Final time update on unmount
          const finalTimeViewed = Math.floor((new Date() - timestamp) / 1000);
          updateDoc(trackingDocRef, {
            [`viewTimes.${viewerId}.${timestamp.toISOString()}`]: finalTimeViewed,
          }).catch(console.error);
        };
      } catch (err) {
        console.error('Error tracking PDF view:', err);
        setError('Failed to load PDF');
        setLoading(false);
      }
    };

    trackView();
  }, [trackingId]);

  // Get client IP (simplified - in production, use a service or cloud function)
  const getClientIP = async () => {
    try {
      // Try to get IP from a service (you can use your own endpoint)
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || 'unknown';
    } catch {
      return 'unknown';
    }
  };

  // Generate a simple viewer ID hash
  const generateViewerId = (str1, str2 = null) => {
    const str = str2 ? `${str1}-${str2}` : str1;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  };

  if (loading) {
    return (
      <div className="track-pdf-container">
        <div className="track-pdf-loading">
          <div className="spinner"></div>
          <p>Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="track-pdf-container">
        <div className="track-pdf-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard/schools')} className="track-pdf-back-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="track-pdf-container">
      <iframe
        src={pdfUrl}
        title="PDF Viewer"
        className="track-pdf-iframe"
      />
    </div>
  );
};

export default TrackPDF;
